import { useQuery } from "@tanstack/react-query";
import { addError } from "@wayflyer/blackbox-fe";
import { Flex, LandingLayout, PageGridCell, PageGridRow, Spinner } from "@wayflyer/flyui";
import type { FundingApplicationSchemas } from "api-types";
import { useIntl } from "react-intl";
import { Navigate } from "react-router";
import { getAIRsDataQueryOptions, getDealOwnerQueryOptions, useSpaNavigate } from "shared-ui";
import { useCustomerAppConfig } from "undercarriage";

import { AIRListNext } from "../../pages/airs-review-page/primary-content/air-list-next/air-list-next";
import { useCreateFundingApplicationMutation } from "../../queries/funding-application/queries";
import { useInReviewDetails } from "../../queries/review-screen/review-screen-queries";
import { NAVIGATION_PATHS } from "../constants";

import { ApplicationStatusCard, type ApplicationStatusCardProps } from "./application-status-card";
import { EmailVerificationCard } from "./email-verification-card";
import { MESSAGES } from "./message-definitions";

const primaryColumn = { md: { span: 8 }, sm: { span: 8 }, xxs: { span: 6 } } as const;
const secondaryColumn = { md: { span: 4 }, sm: { span: 8 }, xxs: { span: 6 } } as const;

export const GetStarted = () => {
  const intl = useIntl();
  const {
    data: { customerDomain, customerUuid },
  } = useCustomerAppConfig();
  const { navigate: companyCreationNavigate } = useSpaNavigate("CompanyCreation");

  const { data: reviewData, isLoading: isReviewLoading } = useInReviewDetails(customerDomain);
  // AIRs only exist while a product request is in progress — skip the fetch for first-time applicants.
  const { data: airsData, isLoading: isAirsLoading } = useQuery({
    ...getAIRsDataQueryOptions(customerDomain),
    enabled: reviewData?.isProductRequestInProgress === true,
    throwOnError: true,
  });
  // Deal owner only matters when we have a manual AIR to render — drives rep-owned vs self-serve copy.
  const { data: dealOwnerData, isLoading: isDealOwnerLoading } = useQuery({
    ...getDealOwnerQueryOptions(customerDomain, reviewData?.productRequestId?.toString()),
    enabled: !!reviewData?.productRequestId && airsData?.hasActiveManualAirs === true,
  });
  const isRepOwned = !!dealOwnerData?.name;
  const createFundingApplication = useCreateFundingApplicationMutation(customerDomain, customerUuid);

  const onApply = () => {
    createFundingApplication
      .mutateAsync()
      .then(() => {
        // Todo: navigate directly to /funding/application/revenue once OBN-202 is complete
        companyCreationNavigate("revenue", { replace: true });
      })
      .catch((error) => {
        addError("Failed to create funding application:", error);
      });
  };

  if (isReviewLoading || isAirsLoading || isDealOwnerLoading || !reviewData) {
    return (
      <LandingLayout key="loading">
        <Spinner size={32} />
      </LandingLayout>
    );
  }

  // Customer has an open product request but the funding application is not yet submitted —
  // redirect them back into the funding application flow to finish what they started.
  if (reviewData.isProductRequestInProgress && !reviewData.submittedDatetime) {
    // Todo: navigate directly to /funding/application/revenue once OBN-202 is complete
    return <Navigate to="/company-creation/revenue" replace />;
  }

  let cardProps: ApplicationStatusCardProps;
  let airListData: FundingApplicationSchemas.AdditionalInformationRequestsData | undefined;

  if (!reviewData.isProductRequestInProgress) {
    cardProps = { state: "no-application", onApply, isApplying: createFundingApplication.isPending };
  } else if (airsData?.hasActiveAirs || airsData?.hasActiveManualAirs) {
    cardProps = { state: "action-required" };
    airListData = airsData;
  } else {
    cardProps = { state: "in-review" };
  }

  return (
    <LandingLayout
      title={intl.formatMessage(MESSAGES.pageHeading)}
      subtitle={intl.formatMessage(MESSAGES.pageSubtitle)}
    >
      <PageGridRow align="start">
        <PageGridCell layout={primaryColumn}>
          <Flex direction="column" gap="3">
            {!reviewData.emailVerified && <EmailVerificationCard emailVerified={false} email={reviewData.email} />}
            {cardProps.state === "action-required" && airListData && (
              <AIRListNext
                airsData={airListData}
                airPath={NAVIGATION_PATHS.GET_STARTED_ADDITIONAL_INFORMATION_REQUEST}
                isRepOwned={isRepOwned}
              />
            )}
            {reviewData.emailVerified && <EmailVerificationCard emailVerified={true} email={reviewData.email} />}
          </Flex>
        </PageGridCell>
        <PageGridCell layout={secondaryColumn}>
          <ApplicationStatusCard {...cardProps} />
        </PageGridCell>
      </PageGridRow>
    </LandingLayout>
  );
};
